"use client"

import React, { useEffect, useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { completeForgotPasswordAction } from "@/actions/forgotPasswordActions"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons"
import { LoaderCircle } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { FormControl, FormField, FormItem, FormMessage, Form as RHFForm } from "@/components/ui/components"
import { Button } from "@/components/ui/components/button"
import { Input } from "@/components/ui/components/input"
import { PasswordToggleInput } from "@/components/ui/components/password-toggle-input"
import { cn } from "@/components/ui/helpers/utils"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import { FormRow } from "@/components/account/shared/FormRow"
import PasswordChecklist from "@/components/common/PasswordChecklist"
import { UbAccountForgotpasswordViewStoryblok } from "@/components/storyblok/component-types"

interface AuthCompleteForgotPasswordClientProps {
  blok: UbAccountForgotpasswordViewStoryblok
  uid: string
  hash: string
  newPasswordTopContent: React.ReactNode
  newPasswordBottomContent: React.ReactNode
  newPasswordSuccessContent: React.ReactNode
}

const makeSchema = (blok?: UbAccountForgotpasswordViewStoryblok) =>
  z
    .object({
      password: z.string().min(1, { message: blok?.label_password_required ?? "" }),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: blok?.label_passwords_do_not_match ?? "",
    })

type FormValues = {
  password: string
  confirmPassword: string
}

export default function AuthCompleteForgotPasswordClient({
  blok,
  uid,
  hash,
  newPasswordTopContent,
  newPasswordBottomContent,
  newPasswordSuccessContent,
}: AuthCompleteForgotPasswordClientProps) {
  const [isPending, startTransition] = useTransition()
  const router = useRouter()

  const form = useForm<FormValues>({
    resolver: zodResolver(makeSchema(blok)),
    defaultValues: { password: "", confirmPassword: "" },
    mode: "onChange",
  })

  const passwordValue = form.watch("password")
  const [passwordChecklistValidIconColor, setPasswordChecklistValidIconColor] = useState("text-gray-400")
  const [passwordChecklistInvalidIconColor, setPasswordChecklistInvalidIconColor] = useState("text-gray-400")
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const hasValue = Boolean(passwordValue && passwordValue.length > 0)
    setPasswordChecklistValidIconColor(hasValue ? "text-green-500" : "text-gray-400")
    setPasswordChecklistInvalidIconColor(hasValue ? "text-red-500" : "text-gray-400")
  }, [passwordValue])

  async function onSubmit(values: FormValues) {
    startTransition(async () => {
      try {
        const res = await completeForgotPasswordAction(uid, hash, values.password)
        if (!res.success) {
          const msg =
            res.error === "invalid_token"
              ? (blok?.label_invalid_link ?? "")
              : res.error === "weak_password"
                ? (blok?.label_invalid_password ?? "")
                : (blok?.label_password_reset_error ?? "")
          toast.error(msg)
          return
        }
        toast.success(blok?.label_password_reset_success ?? "")
        setSuccess(true)
      } catch (e) {
        toast.error(blok?.label_password_reset_error ?? "")
      }
    })
  }

  const { control, handleSubmit } = form

  return (
    <div className={"mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8"}>
      {/*<AccountBackLinkClient label={blok?.label_common_back} />*/}
      <div className="relative">
        <div className={cn("absolute max-w-prose", { hidden: !success })}>{newPasswordSuccessContent}</div>
        <div className={cn("flex max-w-prose flex-col gap-2 lg:gap-6", { invisible: success })}>
          <div className={"flex flex-col gap-2"}>
            {newPasswordTopContent}
            <p className="text-sm text-gray-600">
              <span className="text-red-500">*</span> {blok.label_hint_mandatory_field}
            </p>
          </div>
          <RHFForm {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2 lg:gap-6">
              <FormRow label={blok.label_newpassword} htmlFor={"password"} required>
                <FormField
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className={"w-full"}>
                      <FormControl>
                        <PasswordToggleInput
                          id="password"
                          autoComplete={"new-password"}
                          placeholder={blok?.label_newpassword}
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      <PasswordChecklist
                        className="mt-1 flex flex-wrap items-center justify-start gap-2 text-xs"
                        itemClassName="flex gap-1 items-center pt-0"
                        rules={["minLength", "lowercase", "capital", "specialChar", "number"]}
                        value={passwordValue || ""}
                        minLength={10}
                        messages={{
                          minLength: blok?.password_checklist_minlength,
                          lowercase: blok?.password_checklist_lowercase,
                          capital: blok?.password_checklist_capital,
                          specialChar: blok?.password_checklist_specialchar,
                          number: blok?.password_checklist_number,
                        }}
                        iconSize={16}
                        iconComponents={{
                          ValidIcon: <CheckCircledIcon className={passwordChecklistValidIconColor} />,
                          InvalidIcon: <CrossCircledIcon className={passwordChecklistInvalidIconColor} />,
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormRow>
              <FormRow label={blok.label_newpassword_repeat} htmlFor={"confirmPassword"} required>
                <FormField
                  control={control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem className={"w-full"}>
                      <FormControl>
                        <PasswordToggleInput
                          id="confirmPassword"
                          autoComplete={"new-password"}
                          placeholder={blok?.label_newpassword_repeat}
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormRow>
              <div className="flex justify-end gap-4">
                <Button type="reset" variant="outline" onClick={() => router.back()} disabled={isPending}>
                  {blok?.label_cancel}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && <LoaderCircle className="mr-2 size-4 motion-safe:animate-spin" />}
                  {blok?.label_set_newpassword}
                </Button>
              </div>
            </form>
          </RHFForm>
          {newPasswordBottomContent}
        </div>
      </div>
    </div>
  )
}
